import React, { useCallback, useEffect, useState } from 'react';
import { Box, Alert } from '@mui/material';
import { useApiClient } from '../../config/axios';
import { useCustomer } from '../../contexts/CustomerContext';
import CategoryRail from './CategoryRail';
import ExceptionGrid from './ExceptionGrid';
import { CATEGORY_META, getCategoryMeta } from './categoryMeta';
import {
  fetchExceptionSummary,
  fetchExceptionCategory,
  ExceptionCategory,
  ExceptionSummary,
  ExceptionDetail,
} from './ExceptionAnalysis.api';

// Layout: left rail (300px, category selector + counts) + right pane (grid).
// State kept local — no cross-tab context needed for v1.
const ExceptionAnalysis: React.FC = () => {
  const apiClient = useApiClient();
  const { currentCustomer } = useCustomer();

  const [selected, setSelected] = useState<ExceptionCategory>('metric-gaps');
  const [summary, setSummary] = useState<ExceptionSummary | null>(null);
  const [detail, setDetail] = useState<ExceptionDetail | null>(null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadSummary = useCallback(async () => {
    if (!currentCustomer) return;
    setLoadingSummary(true);
    setError(null);
    try {
      const s = await fetchExceptionSummary(apiClient);
      setSummary(s);
    } catch (e: any) {
      setError(e?.response?.data?.error || e.message || 'Failed to load summary');
    } finally {
      setLoadingSummary(false);
    }
  }, [apiClient, currentCustomer]);

  const loadDetail = useCallback(
    async (category: ExceptionCategory) => {
      if (!currentCustomer) return;
      setLoadingDetail(true);
      setDetail(null);
      setError(null);
      try {
        const d = await fetchExceptionCategory(apiClient, category);
        setDetail(d);
      } catch (e: any) {
        setError(e?.response?.data?.error || e.message || 'Failed to load exceptions');
      } finally {
        setLoadingDetail(false);
      }
    },
    [apiClient, currentCustomer]
  );

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { loadDetail(selected); }, [loadDetail, selected]);

  const handleRefresh = useCallback(() => {
    loadSummary();
    loadDetail(selected);
  }, [loadSummary, loadDetail, selected]);

  if (!currentCustomer) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          Select a tenant + environment in the header to view exceptions.
        </Alert>
      </Box>
    );
  }

  const meta = getCategoryMeta(selected) || CATEGORY_META[0];

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 200px)', minHeight: 500, overflow: 'hidden' }}>
      <CategoryRail
        summary={summary}
        loadingSummary={loadingSummary}
        selected={selected}
        onSelect={setSelected}
        onRefresh={handleRefresh}
      />
      <ExceptionGrid
        meta={meta}
        data={detail}
        loading={loadingDetail}
        error={error}
      />
    </Box>
  );
};

export default ExceptionAnalysis;
